// ============================================================
// PERRY — THE REGALIA ENGINE (pure logic, no React, no Firebase)
//
// WHY THIS FILE EXISTS, 2026-07-28 (caP'n): "just cause Perry is a
// Beb doesn't mean he can't access immense power, time for him to
// power up."
//
// Ten powers now, five carried, and every one of them has live
// state that ticks: charges that wait, clocks that run out, a
// landscape that is briefly yours. That state could have gone into
// the director — the director already owns `vitals` — and it very
// nearly did. It lives here instead for the same reason
// `qteEngine` exists: the director is a 937-line rAF loop with
// five exit paths per beat, and every piece of sticky state added
// to it has to be correctly un-set on all five, forever, by
// somebody who remembers. That is how you lose a playtest.
//
// So: ONE object, ONE tick, ONE place a power is spoken and ONE
// place it is spent. The director calls seven functions and holds
// no power state of its own.
//
// THE LAWS THIS FILE OBEYS:
//   • R-UNIVERSAL — one gate. `speak()` is the only way any power
//     is ever activated, by anyone, for any reason. No handler
//     decides legality inline. Ever.
//   • THE TWO-GATE RULE (Blueprint §16) — owning is checked
//     against your MAX pool (the constellation, upstream of here);
//     firing is checked against your CURRENT pool (Gate B, below).
//   • A POWER MAY NEVER KILL YOU. The Anima check is strictly
//     `anima > costAnima`, never `>=`. Three places, no exceptions.
//   • THE EXCEPTION LAW — every power dispatches on a STRING
//     (`fx`), never on its id. An eleventh power is a data row in
//     perryData and, at most, one case in this file.
//   • DETERMINISM — no Math.random anywhere. Nothing in here rolls.
//   • THE SMALL NUMBER LAW — every magnitude is a flat single
//     digit. The only fractions are temporal (clock multipliers),
//     which the law's own boundary table exempts.
// ============================================================

import { REGALIA, RULES } from "./perryData";

// ── THE LIVE STATE ───────────────────────────────────────────
// One object per RUN. Everything on it is either a count, a
// deadline in performance.now() ms, or null.
//
//   shadow     Intangible charges — blows that pass through
//   echo       free hits armed — one clean Anima, past Lyf
//   mirage     failure-insurance charges — a MISS becomes PERFECT
//   answer     U'hlti charges — the NEXT strike, whatever it is
//   graceUntil King's Grace deadline
//   emp        the Emperium: { until, left, theme }
//   crownLeft  free activations remaining this foe (Hollow Crown)
//   poolLeft   Mirror Pool charges remaining this SOLREND
//   hearthUsed Bank the Hearth is once per foe
//   note       the last thing worth saying out loud, or null
export function freshPowers(soul) {
  return {
    shadow: 0,
    echo: 0,
    mirage: 0,
    answer: 0,
    graceUntil: 0,
    emp: { until: 0, left: 0, theme: null },
    crownLeft: 0,
    // The Pool spends across the whole Solrend, deliberately —
    // like A Page of Names, and for the same reason. A charge that
    // refills every fight is a formality you spend at the top of
    // every fight. A charge that does not is a decision.
    poolLeft: soul?.tomes?.tome_mirror_pool ? 1 : 0,
    hearthUsed: false,
    note: null,
  };
}

// Per-encounter reset. Called from startEncounter, beside the Chao
// refill, because these are breath-shaped things and breath refills.
//
// WHAT SURVIVES A FOE AND WHAT DOES NOT is the whole balance of
// this layer: armed charges DIE with the encounter (you do not
// stockpile a Mirage across three fights and walk into the boss
// holding six), and per-fight allowances RESET (the Crown is
// generous once a foe, every foe, forever — that is what you paid
// forty-five Kindling for).
// ══════════════════════════════════════════════════════════════
//  TWO RESETS NOW — the foe's, and the fire's (2026-07-29)
// ══════════════════════════════════════════════════════════════
// caP'n: "let's make the Chao actually work so I can cast an Impera
//  before battle and have the effect work."
//
// That could not happen while this one function wiped every armed
// charge at encounter start — you would arm Shadow on the road and
// `startEncounter` would erase it a second later. But the fix is
// not "stop resetting"; it is noticing that ONE function was doing
// TWO jobs that used to have the same clock and no longer do.
//
// The clock changed underneath it. Chao used to refill at every
// foe, so "armed charges die at every foe" cost you nothing —
// you got the breath back immediately. Chao now refills only at a
// FIRE, so a charge that died at every foe would mean breath spent
// on the road is breath thrown away, and nobody would ever pre-cast
// anything. The reset has to follow the currency.
//
//   encounterPowers — the FOE's reset. Per-foe generosity only.
//   kitPowers       — the FIRE's reset. Everything you were holding.
//
// WHAT SURVIVES A FIGHT NOW: shadow, echo, mirage, answer, the
// King's Grace clock, the Emperium's domain. Arm it at a fire,
// carry it into whatever is next. If you never spend it, it burns
// at the next fire along with the breath that bought it — so
// hoarding is not free, it is just a different bet.
export function encounterPowers(P, soul) {
  // Per-FOE allowances. The Fare Law's generosity, renewed against
  // every enemy: you paid once to light the fire, and the fire
  // keeps burning against each new thing that walks up.
  P.crownLeft = soul?.tomes?.tome_hollow_crown ? RULES.CROWN_FREE : 0;
  P.hearthUsed = false;
  P.note = null;
  return P;
}

// The FIRE's reset. Called by the director's kitUp() and by
// nothing else. What you were carrying goes out with the embers.
export function kitPowers(P, soul) {
  P.shadow = 0;
  P.echo = 0;
  P.mirage = 0;
  P.answer = 0;
  P.graceUntil = 0;
  P.emp = { until: 0, left: 0, theme: null };
  return encounterPowers(P, soul);
}

// ══════════════════════════════════════════════════════════════
//  THE PAUSE — added 2026-07-29 with the Tutorial's strike report
// ══════════════════════════════════════════════════════════════
// Every clock in this file is an ABSOLUTE deadline in
// performance.now() ms, which is the right shape for a game that
// never stops — and exactly the wrong shape for one that does.
// The tutorial's report card freezes the road between fights, and
// wall-clock time does not freeze with it: a King's Grace spoken
// on the way in would quietly burn its whole eight seconds while
// the player reads a card.
//
// So the director hands the paused duration back here and every
// deadline moves forward by it. Counts and flags are untouched —
// they were never times.
//
// A HELPER, NOT A SUBSYSTEM. If a future power grows a clock, it
// gets one line here, and the pause keeps being correct. (That is
// the whole reason this is a function in this file rather than
// four `+= d` lines in the director: the director does not know
// what a power is, and this is the file that does.)
export function shiftPowerClocks(P, d) {
  if (!P || !d) return P;
  if (P.graceUntil) P.graceUntil += d;
  if (P.emp && P.emp.until) P.emp.until += d;
  return P;
}

// ── GATE B — can this be spoken RIGHT NOW? ───────────────────
// Reasons, not booleans. A power that refuses silently is a power
// the player believes is broken, and this game has no HUD to
// explain itself with — the refusal line IS the explanation.
export function canSpeak(P, id, v, carried) {
  const p = REGALIA[id];
  if (!p) return { ok: false, why: null };
  if (!carried) return { ok: false, why: null };
  const free = P.crownLeft > 0 || (P.poolLeft > 0 && v.chao < p.costChao);
  if (!free && v.chao < p.costChao) {
    return { ok: false, why: "You reach for it and there is no breath behind it." };
  }
  // THE LAW: a power may never be the thing that kills you.
  // Strictly greater than. Never >=. Not once, anywhere.
  if (v.anima <= p.costAnima) {
    return { ok: false, why: "It would cost you the last of you. It refuses to be spoken." };
  }
  if (p.fx === "hearth" && P.hearthUsed) {
    return { ok: false, why: "The hearth is already banked. Once a foe." };
  }
  if (p.fx === "emperium" && P.emp.until) {
    return { ok: false, why: "You are already standing in it." };
  }
  return { ok: true, why: null, free };
}

// ── SPEAKING A POWER — THE ONE GATE ──────────────────────────
// Returns { ok, say, sight, refillChao, slow } — a DESCRIPTION of
// what happened, never a side effect on anything but P and v. The
// director does the two things only the director can do (refill
// Chao to Max, hand a Sight charge to the coals) and nothing else.
//
// `ctx` carries what the engine cannot know: the clock, the Soul's
// species (for the Emperium's sky), and the Anima ceiling.
export function speak(P, id, v, ctx = {}) {
  const p = REGALIA[id];
  if (!p) return { ok: false };
  const t = ctx.now || 0;
  const gate = canSpeak(P, id, v, true);
  if (!gate.ok) return { ok: false, say: gate.why };

  // ── PAYMENT, and the two ways out of paying ──
  // The Hollow Crown goes first because it is the one you bought;
  // the Mirror Pool is the safety net under it and only catches a
  // power you genuinely could not afford. Order matters: reversed,
  // the Pool would eat itself on the free one.
  let paid = "chao";
  if (P.crownLeft > 0) {
    P.crownLeft -= 1;
    paid = "crown";
  } else if (v.chao < p.costChao && P.poolLeft > 0) {
    P.poolLeft -= 1;
    paid = "pool";
  } else {
    v.chao -= p.costChao;
  }
  if (p.costAnima) v.anima -= p.costAnima;

  const out = {
    ok: true,
    // THE GROWTH LAW's ledger. Chao you did not actually spend was
    // not spent — a Crown-funded power leaves your restraint
    // intact, which is precisely what makes the Crown worth 45 ⋔.
    chaoSpent: paid === "chao" ? p.costChao : 0,
    say: null, sight: 0, refillChao: false,
  };

  // ── THE DISPATCH — on `fx`, never on the id ──
  switch (p.fx) {
    case "still":
      v.slowLeft = 3;
      out.say = "You breathe out. The fire keeps moving; it simply stops being in a hurry.";
      break;

    case "eye":
      v.foresight = RULES.SIGHT_THIRD_EYE;
      out.sight = RULES.SIGHT_THIRD_EYE;
      out.say = "Two strikes ahead, the fire shows you its shape — and the coals, briefly, remember what they are called.";
      break;

    case "pact":
      out.refillChao = true;
      out.say = "You dim your own light to fill your lungs. Life is a currency here, and you just spent some.";
      break;

    case "shadow":
      P.shadow += 1;
      out.say = "You stop being entirely here. The next thing that reaches for you is going to be surprised.";
      break;

    case "echo":
      P.echo += 1;
      out.say = "Something behind your answer clears its throat. It intends to say the same thing again.";
      break;

    case "mirage":
      P.mirage += 1;
      out.say = "There are two of you now, and one of them is a lie, and the fire does not know which.";
      break;

    case "grace":
      // THE FARE LAW, quietly: re-speaking it EXTENDS from now
      // rather than stacking a second clock. You paid to light the
      // fire; the fire keeps burning. Two clocks would be a bug
      // wearing a feature's coat.
      P.graceUntil = t + (p.ms || 8000) * (ctx.hourMult || 1);
      out.say = "Something very old and very patient looks at the road, and the road slows down for you specifically.";
      break;

    case "hearth":
      P.hearthUsed = true;
      v.anima = Math.min(ctx.animaCap || v.anima + 1, v.anima + 1);
      out.say = "You bank the breath instead of spending it. It comes back as heat, and heat is life here.";
      break;

    case "answer":
      P.answer += 1;
      out.say = "You stop thinking about the next one. There is no need. It is already answered.";
      break;

    case "emperium": {
      const th = ctx.theme || null;
      P.emp = {
        until: t + (p.ms || 20000) * (ctx.hourMult || 1),
        left: p.parries || 3,
        theme: th,
      };
      out.say = th?.line || "The road stops being the road.";
      out.emperium = true;
      break;
    }

    default:
      // THE EXCEPTION LAW's own escape hatch. An `fx` nobody has
      // written a case for costs its Chao and says so honestly,
      // rather than silently eating the breath — which is how you
      // ship a power that does nothing for three weeks.
      out.say = `${p.name} answers, and nothing in the world seems to change. (Unwired: ${p.fx})`;
      break;
  }

  if (paid === "crown") out.say = `${out.say} (The Hollow Crown pays.)`;
  if (paid === "pool") out.say = `${out.say} (The Mirror Pool speaks it for you, once.)`;
  P.note = out.say;
  return out;
}

// ── THE TICK — clocks only, once per frame ───────────────────
// Returns true if something CLOSED this frame, so the director can
// say a line about it. Nothing else in this file is time-driven.
export function tickPowers(P, t) {
  let closed = null;
  if (P.graceUntil && t >= P.graceUntil) {
    P.graceUntil = 0;
    closed = "The patience runs out. You are a beb again, and the road remembers it.";
  }
  if (P.emp.until && t >= P.emp.until) {
    const lost = P.emp.left;
    P.emp = { until: 0, left: 0, theme: null };
    // "if not parrys in that time they're gone, not saved" — caP'n.
    // The unspent ones are named on the way out ON PURPOSE. A cost
    // the player does not see paid is a cost they never learn from.
    closed = lost > 0
      ? `The road comes back. ${lost} answer${lost > 1 ? "s" : ""} you never spent go out with it — nothing is saved.`
      : "The road comes back, and it has nothing left to give you. You spent all three.";
  }
  return closed;
}

// ── THE WIND MULTIPLIER — how slow the world is right now ────
// Multiplies the wind-up scale, so BIGGER is slower. Stacks
// multiplicatively with Still the Flame, which the director
// applies through windScale as it always has. Two different
// powers slowing the same clock should compound; they are two
// different clocks being bent, not one clock being bent twice.
export function windMultiplier(P, t) {
  let m = 1;
  if (P.graceUntil && t < P.graceUntil) m *= REGALIA.p_perry_kings_grace.slow || 1.55;
  if (P.emp.until && t < P.emp.until) m *= REGALIA.e_perry_emperium.slow || 1.12;
  return m;
}

// ── THE CONVERSION — the one place a grade is overruled ──────
// Called by the director BEFORE anything else looks at a grade,
// including the Pyre Ladle. Returns the grade that actually
// happened, and mutates P to spend whatever paid for it.
//
// THE ORDER IS THE DESIGN, and it is the strictest thing here:
//
//   1. THE ANSWER (U'hlti) — spends on the NEXT strike whatever
//      that strike was going to be. You chose the moment; the
//      moment is now. It goes first because it is the only one
//      you deliberately aimed.
//   2. THE EMPERIUM — the road is answering for you. It fires on
//      anything that is not already PERFECT, because three
//      auto-answers that decline to upgrade a GOOD would be three
//      auto-answers that sometimes do nothing.
//   3. SHADOW — you are not there. Checked before Mirage because
//      Intangibility is cheaper (1 Chao) and does strictly less
//      (it answers nothing); spending the expensive insurance
//      while the cheap escape is armed would be a robbery.
//   4. MIRAGE — the patient one. It waits for an actual failure
//      and only then turns it into a perfect answer with teeth.
//
// Returns { g, bite, intangible, say } — `bite` means "this
// conversion owes the foe a free hit," which the director resolves
// through its own one bite() function. Nothing here touches the
// foe. It cannot: the Lyf Cycle lives in the director and there
// is exactly one of it.
export function convertGrade(P, g, t) {
  const out = { g, bite: 0, intangible: false, say: null };

  if (P.answer > 0) {
    P.answer -= 1;
    out.g = "PERFECT";
    out.say = g === "PERFECT" ? null : "The Answer arrives before the strike does.";
    return out;
  }

  if (P.emp.until && t < P.emp.until && P.emp.left > 0 && g !== "PERFECT") {
    P.emp.left -= 1;
    out.g = "PERFECT";
    out.say = `The road answers it for you. ${P.emp.left} left.`;
    return out;
  }

  // Only failures reach the last two. A parry you actually landed
  // never spends a charge — that is the difference between
  // insurance and a subscription.
  if (g !== "MISS") return out;

  if (P.shadow > 0) {
    P.shadow -= 1;
    out.intangible = true;
    out.say = "It goes through you. There was nothing there to hit.";
    return out;
  }

  if (P.mirage > 0) {
    P.mirage -= 1;
    out.g = "PERFECT";
    out.bite = 1;
    out.say = "It commits to the one that was not you. Yours answers from the side it was not watching.";
    return out;
  }

  return out;
}

// ── ECHO — the free hit, spent on a landed answer ────────────
// Separate from convertGrade on purpose: conversion decides WHAT
// GRADE HAPPENED, and Echo decides what a grade that already
// happened is WORTH. Folding them together is how you end up with
// an Echo that fires on a Mirage-converted miss and hands out two
// free hits for one Chao.
//
// Returns the number of free hits owed (0 or 1). PERFECT and GOOD
// both qualify — see the registry comment for why.
export function spendEcho(P, g) {
  if (P.echo <= 0) return 0;
  if (g !== "PERFECT" && g !== "GOOD") return 0;
  P.echo -= 1;
  return 1;
}

// ── THE HOUR — the Long Hour's ATV multiplier ────────────────
// A separate tiny function because it is asked at SPEAK time (it
// sets the deadline) and the ATV that owns it can be snuffed by a
// feint between one speaking and the next, exactly like the
// Kindled Blade. The director owns the flame; this owns the maths.
export const hourMult = (lit) => (lit ? 1.5 : 1);

// ── WHAT THE HUD IS ALLOWED TO SAY ───────────────────────────
// A flat, cheap snapshot — no objects the overlay could hold onto
// and read a frame late. Everything here is a fact about something
// that has ALREADY happened, which is the only thing this title
// permits on the glass.
export function powerHud(P, t) {
  return {
    shadow: P.shadow,
    echo: P.echo,
    mirage: P.mirage,
    answer: P.answer,
    graceMs: P.graceUntil ? Math.max(0, P.graceUntil - t) : 0,
    empMs: P.emp.until ? Math.max(0, P.emp.until - t) : 0,
    empLeft: P.emp.until ? P.emp.left : 0,
    empTheme: P.emp.until ? P.emp.theme : null,
    crownLeft: P.crownLeft,
    poolLeft: P.poolLeft,
  };
}
